import { cn } from "@/lib/utils";
import { ArrowRight, Check } from "lucide-react";
import {
  accentClasses,
  type IndustryAccent,
} from "../industryTheme/industryTheme.util";
import StatusPill, { type StatusTone } from "../statusPill/statusPill.component";

interface BillingFlowBox {
  // Small uppercase label, e.g. "Your counter".
  kicker: string;
  title: string;
  note: string;
}

export interface BillingFlow {
  counter: BillingFlowBox;
  // Arrow label from the counter to Fydo, e.g. "phone number + bill amount".
  toFydo: string;
  fydo: { kicker: string; items: string[] };
  // Arrow label from Fydo to the customer.
  toCustomer: string;
  customer: BillingFlowBox;
}

export interface BillingFlowCard {
  title: string;
  text: string;
  status?: { label: string; tone: StatusTone };
  // "outline" = white with a border (the recommended start), "soft" = cream.
  variant?: "outline" | "soft";
}

interface BillingFlowPanelProps {
  flow: BillingFlow;
  cards: BillingFlowCard[];
  accent?: IndustryAccent;
}

// "Billing and your existing process" - the preview's `.bp`. A three-box
// flow (your counter -> the Fydo layer -> your customer) with labelled
// dashed arrows, then cards on how to start and what to know.
const BillingFlowPanel = ({ flow, cards, accent = "brand" }: BillingFlowPanelProps) => {
  const tone = accentClasses[accent];

  const arrow = (label: string) => (
    <div className="grid min-h-[50px] content-center justify-items-center gap-1.5 text-center lg:min-w-[84px]">
      <em className="text-[11.5px] font-bold text-ink-muted not-italic">{label}</em>
      <span aria-hidden="true" className="flex items-center lg:w-full">
        <span
          className={cn(
            "h-[30px] border-l-[3px] border-dashed lg:h-0 lg:flex-1 lg:border-t-[3px] lg:border-l-0",
            tone.border,
          )}
        />
        <ArrowRight
          className={cn("hidden size-4 shrink-0 lg:block", tone.text)}
          strokeWidth={3}
        />
      </span>
    </div>
  );

  const box = (item: BillingFlowBox, className: string) => (
    <div className={cn("grid content-center gap-1.5 rounded-[26px] p-6", className)}>
      <span className="text-[12px] font-extrabold tracking-[0.08em] uppercase opacity-85">
        {item.kicker}
      </span>
      <b className="text-[1.1rem] leading-snug font-bold">{item.title}</b>
      <small className="text-[13px] text-ink-muted">{item.note}</small>
    </div>
  );

  return (
    <div className="grid gap-[22px]">
      <div className="grid gap-3 lg:grid-cols-[1fr_auto_1.15fr_auto_1fr] lg:items-stretch">
        {box(flow.counter, "border-2 border-dashed border-line bg-white text-ink")}
        {arrow(flow.toFydo)}
        <div className="grid content-center gap-1.5 rounded-[26px] bg-ink p-6 text-white shadow-lift">
          <span className="text-[12px] font-extrabold tracking-[0.08em] uppercase opacity-85">
            {flow.fydo.kicker}
          </span>
          <ul className="mt-1.5 grid gap-2 text-[15px] font-semibold">
            {flow.fydo.items.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <Check
                  className="size-4 shrink-0 text-primary"
                  strokeWidth={3}
                  aria-hidden="true"
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
        {arrow(flow.toCustomer)}
        {box(flow.customer, cn(tone.soft, "text-ink"))}
      </div>

      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <article
            key={card.title}
            className={cn(
              "grid content-start gap-2 rounded-[26px] p-[26px]",
              card.variant === "soft"
                ? "bg-cream"
                : "border border-line bg-white",
            )}
          >
            {card.status && (
              <StatusPill label={card.status.label} tone={card.status.tone} />
            )}
            <h3 className="text-[1.1rem] leading-snug font-bold text-ink">
              {card.title}
            </h3>
            <p className="text-[15px] leading-relaxed text-ink-muted">
              {card.text}
            </p>
          </article>
        ))}
      </div>
    </div>
  );
};

export default BillingFlowPanel;
